import clsx from "clsx";
import { ChevronLeft, ChevronRight, type LucideIcon } from "lucide-react";
import type { RefObject } from "react";
import { Link, useLocation } from "wouter";

import { resolveThemeAsset } from "@/theme/assets";
import type { ThemeV1 } from "@/theme/schema";

import {
  activeFor,
  primaryDestinations,
  worldDestinations,
} from "./navigation";
import { ProductModeSwitcher } from "./ProductModeSwitcher";

export function ProductSidebar({
  collapsed,
  mobileOpen,
  onClose,
  onPrefetch,
  onToggleCollapsed,
  theme,
  toggleRef,
}: {
  collapsed: boolean;
  mobileOpen: boolean;
  onClose: () => void;
  onPrefetch?: (path: string) => void;
  onToggleCollapsed: () => void;
  theme: ThemeV1;
  toggleRef: RefObject<HTMLButtonElement | null>;
}) {
  const [location] = useLocation();
  const sidebarBackground = resolveThemeAsset(
    theme.assets.sidebarBackgroundKey,
  );

  return (
    <>
      <button
        aria-label="Close navigation"
        className={clsx("v2-sidebar-backdrop", mobileOpen && "is-visible")}
        onClick={onClose}
        tabIndex={mobileOpen ? 0 : -1}
        type="button"
      />
      <aside
        aria-label={`${theme.brand.displayName} navigation`}
        className={clsx(
          "v2-sidebar",
          collapsed && "is-collapsed",
          mobileOpen && "is-mobile-open",
        )}
        id="v2-product-sidebar"
      >
        {sidebarBackground ? (
          <img
            alt=""
            aria-hidden
            className="v2-sidebar__background"
            src={sidebarBackground}
          />
        ) : null}
        <span className="v2-sidebar__glow-line" aria-hidden />
        <span className="v2-sidebar__glow-blur" aria-hidden />
        <button
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="v2-sidebar__toggle"
          onClick={onToggleCollapsed}
          ref={toggleRef}
          type="button"
        >
          <ChevronLeft aria-hidden />
          <ChevronRight aria-hidden />
        </button>

        <div className="v2-sidebar__content">
          <ProductModeSwitcher
            collapsed={collapsed}
            onNavigate={onClose}
            theme={theme}
          />

          <SidebarGroup
            destinations={primaryDestinations}
            label={theme.brand.displayName}
            location={location}
            onPrefetch={onPrefetch}
            onSelect={onClose}
            showLabel={false}
          />
          <SidebarGroup
            destinations={worldDestinations}
            label="World"
            location={location}
            onPrefetch={onPrefetch}
            onSelect={onClose}
          />
        </div>
      </aside>
    </>
  );
}

function SidebarGroup({
  destinations,
  label,
  location,
  onPrefetch,
  onSelect,
  showLabel = true,
}: {
  destinations: ReadonlyArray<{
    href: string;
    label: string;
    icon: LucideIcon;
  }>;
  label: string;
  location: string;
  onPrefetch?: (path: string) => void;
  onSelect: () => void;
  showLabel?: boolean;
}) {
  return (
    <nav
      aria-label={label}
      className={clsx(
        "v2-sidebar__group",
        !showLabel && "v2-sidebar__group--unlabelled",
      )}
    >
      {showLabel ? (
        <span className="v2-sidebar__group-label">{label}</span>
      ) : null}
      {destinations.map(({ href, label: itemLabel, icon: Icon }) => (
        <Link
          aria-current={activeFor(location, href) ? "page" : undefined}
          aria-label={itemLabel}
          className={clsx(
            "v2-sidebar__link",
            activeFor(location, href) && "is-active",
          )}
          href={href}
          key={href}
          onFocus={() => onPrefetch?.(href)}
          onMouseEnter={() => onPrefetch?.(href)}
          onClick={onSelect}
        >
          <span className="v2-sidebar__link-icon">
            <Icon aria-hidden />
          </span>
          <span className="v2-sidebar__link-label">{itemLabel}</span>
        </Link>
      ))}
    </nav>
  );
}
